const AUTH_RETURN_TO_KEY = 'authReturnTo';
const DEFAULT_RETURN_TO = '/upcoming-lots';
const MAX_RETURN_TO_LENGTH = 500;

export function validateReturnTo(path) {
    if (typeof path !== 'string') {
        return null;
    }

    const trimmed = path.trim();
    if (!trimmed || trimmed.length > MAX_RETURN_TO_LENGTH) {
        return null;
    }

    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
        return null;
    }

    const lower = trimmed.toLowerCase();
    if (lower.includes('http:') || lower.includes('https:') || trimmed.includes('\\')) {
        return null;
    }

    const queryStart = trimmed.indexOf('?');
    const ampersand = trimmed.indexOf('&');
    if (ampersand !== -1 && (queryStart === -1 || ampersand < queryStart)) {
        return null;
    }

    return trimmed;
}

export function extractReturnToParam(search) {
    if (typeof search !== 'string' || !search) {
        return null;
    }

    const query = search.startsWith('?') ? search.slice(1) : search;
    const value = new URLSearchParams(query).get('returnTo');

    return value || null;
}

function normalizeSearchInput(searchInput) {
    if (typeof searchInput === 'string') {
        return searchInput.startsWith('?') ? searchInput : `?${searchInput}`;
    }

    if (searchInput && typeof searchInput.toString === 'function') {
        const serialized = searchInput.toString();
        return serialized ? `?${serialized}` : '';
    }

    return '';
}

function readStoredAuthReturnTo() {
    for (const storage of [sessionStorage, localStorage]) {
        try {
            const validated = validateReturnTo(storage.getItem(AUTH_RETURN_TO_KEY));
            if (validated) {
                return validated;
            }
        } catch {
            // ignore storage errors
        }
    }

    return null;
}

function writeStoredAuthReturnTo(value) {
    for (const storage of [sessionStorage, localStorage]) {
        try {
            storage.setItem(AUTH_RETURN_TO_KEY, value);
        } catch {
            // ignore storage errors
        }
    }
}

function clearStoredAuthReturnTo() {
    for (const storage of [sessionStorage, localStorage]) {
        try {
            storage.removeItem(AUTH_RETURN_TO_KEY);
        } catch {
            // ignore storage errors
        }
    }
}

export function buildPaymentDetailsUrl({ planId, period, tryForFree }) {
    const params = new URLSearchParams({
        planId: String(planId),
        period: period || 'monthly',
    });

    if (tryForFree) {
        params.set('tryForFree', '1');
    }

    return `/payment-details?${params.toString()}`;
}

export function saveAuthReturnTo(returnTo) {
    const validated = validateReturnTo(returnTo);
    if (!validated) {
        return null;
    }

    writeStoredAuthReturnTo(validated);

    return validated;
}

export function readAuthReturnTo(searchInput) {
    const fromQuery = extractReturnToParam(normalizeSearchInput(searchInput));
    const validatedQuery = validateReturnTo(fromQuery);
    if (validatedQuery) {
        saveAuthReturnTo(validatedQuery);
        return validatedQuery;
    }

    const fromStorage = readStoredAuthReturnTo();
    if (fromStorage) {
        return fromStorage;
    }

    return DEFAULT_RETURN_TO;
}

export function appendReturnTo(path, returnTo) {
    const validated = validateReturnTo(returnTo) || DEFAULT_RETURN_TO;
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}returnTo=${encodeURIComponent(validated)}`;
}

export function clearAuthReturnTo() {
    clearStoredAuthReturnTo();
}
